<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { RouterLink } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores } = proxy;

// Accéder au store du panier via le proxy
const cartStore = $stores?.cart;

const promoCode = ref('');

// Charger le panier au montage du composant
onMounted(() => {
  cartStore?.loadCart();
});

// Articles du panier depuis le store
const cartItems = computed(() => cartStore?.state?.items || []);

// Mise à jour de la quantité via le store
const updateQuantity = (id, delta) => {
  const item = cartItems.value.find((i) => i.id === id);
  if (item) {
    const newQuantity = Math.max(1, item.quantity + delta);
    cartStore?.updateQuantity(id, newQuantity);
  }
};

// Suppression d'article via le store
const removeItem = (id) => {
  cartStore?.removeItem(id);
};

// Application du code promo via le store
const applyPromoCode = () => {
  if (promoCode.value.trim()) {
    cartStore?.applyPromoCode(promoCode.value);
  }
};

// Totaux depuis le store
const subtotal = computed(() => cartStore?.state?.subtotal || 0);
const total = computed(() => cartStore?.state?.total || 0);
const itemCount = computed(() => cartStore?.getItemCount() || 0);
</script>

<template>
  <div class="min-h-screen" style="background: #0a0a0b">
    <div class="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <!-- Page Header -->
      <div class="mb-12">
        <h1
          class="font-black text-4xl md:text-5xl mb-4 text-white"
          style="font-family: 'Outfit', sans-serif"
        >
          <span
            style="
              background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            "
            >Panier</span
          >
        </h1>
        <p class="text-white/60 text-lg">
          {{ itemCount }} article{{ itemCount > 1 ? 's' : '' }}
        </p>
      </div>

      <!-- Breadcrumb -->
      <nav
        aria-label="Breadcrumb"
        class="flex items-center text-sm text-white/50 mb-10"
      >
        <RouterLink class="hover:text-white transition-colors" to="/"
          >Accueil</RouterLink
        >
        <span class="mx-2">/</span>
        <span class="text-white">Panier</span>
      </nav>

      <!-- Empty Cart -->
      <div v-if="cartItems.length === 0" class="text-center py-20 ">
        <div
          class="w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-8"
          style="
            background: rgba(55, 182, 233, 0.1);
            border: 2px solid rgba(55, 182, 233, 0.2);
          "
        >
          <div class="border m-auto w-8">
            <span
              class="material-icons-outlined text-6xl"
              style="color: #37b6e9"
              >shopping_cart_outlined</span
            >
          </div>
        </div>
        <h2
          class="text-3xl font-bold mb-4 text-white"
          style="font-family: 'Outfit', sans-serif"
        >
          Votre panier est vide
        </h2>
        <p class="text-white/50 mb-8 max-w-md mx-auto">
          Parcourez notre boutique et découvrez nos offres d'abonnements
          premium.
        </p>
        <RouterLink
          to="/boutique"
          class="inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold transition-all hover:scale-105"
          style="
            background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);
            color: white;
            box-shadow: 0 4px 20px rgba(55, 182, 233, 0.3);
          "
        >
          <span class="material-icons-outlined">shopping_bag</span>
          Découvrir la Boutique
        </RouterLink>
      </div>

      <!-- Cart Content -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Cart Items -->
        <div class="lg:col-span-2 space-y-6">
          <div
            v-for="item in cartItems"
            :key="item.id"
            class="rounded-2xl p-6 flex gap-6 transition-all hover:scale-[1.01]"
            style="
              background: rgba(26, 26, 29, 0.8);
              border: 1px solid rgba(255, 255, 255, 0.06);
            "
          >
            <!-- Product Image -->
            <div
              class="w-32 h-32 rounded-xl flex items-center justify-center flex-shrink-0 relative"
              style="background: rgba(124, 58, 237, 0.08)"
            >
              <img
                v-if="item.image"
                :alt="item.name"
                class="w-20"
                :src="item.image"
              />
              <span
                v-else
                class="material-icons-outlined text-4xl text-white/30"
                >subscriptions</span
              >
            </div>

            <!-- Product Info -->
            <div class="flex-1">
              <div class="flex justify-between items-start mb-3">
                <div>
                  <h3
                    class="font-bold text-lg mb-1 text-white"
                    style="font-family: 'Outfit', sans-serif"
                  >
                    {{ item.name || item.title }}
                  </h3>
                  <p class="text-sm text-white/50">
                    {{
                      item.options?.duration ||
                      item.options?.variant ||
                      'Durée standard'
                    }}
                  </p>
                </div>
                <button
                  @click="removeItem(item.id)"
                  class="text-white/40 hover:text-red-400 transition-colors"
                >
                  <span class="material-icons-outlined">delete_outline</span>
                </button>
              </div>

              <!-- Quantity & Price -->
              <div class="flex items-center justify-between mt-4">
                <div
                  class="flex items-center gap-3 p-1 rounded-xl"
                  style="
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                  "
                >
                  <button
                    @click="updateQuantity(item.id, -1)"
                    class="w-8 h-8 flex items-center justify-center rounded-lg transition-all hover:scale-105"
                    style="background: rgba(255, 255, 255, 0.05)"
                  >
                    <span class="material-icons-outlined text-sm text-white"
                      >remove</span
                    >
                  </button>
                  <span class="font-semibold text-white w-8 text-center">{{
                    item.quantity
                  }}</span>
                  <button
                    @click="updateQuantity(item.id, 1)"
                    class="w-8 h-8 flex items-center justify-center rounded-lg transition-all hover:scale-105"
                    style="background: rgba(255, 255, 255, 0.05)"
                  >
                    <span class="material-icons-outlined text-sm text-white"
                      >add</span
                    >
                  </button>
                </div>
                <p
                  class="font-black text-xl text-white"
                  style="font-family: 'Outfit', sans-serif"
                >
                  {{ (item.price * item.quantity).toLocaleString() }} CFA
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="lg:col-span-1">
          <div
            class="rounded-3xl p-8 sticky top-28"
            style="
              background: rgba(26, 26, 29, 0.8);
              backdrop-filter: blur(20px);
              border: 1px solid rgba(255, 255, 255, 0.06);
            "
          >
            <h2
              class="text-xl font-bold mb-6 text-white"
              style="font-family: 'Outfit', sans-serif"
            >
              Résumé de la commande
            </h2>

            <!-- Totals -->
            <div class="space-y-4 mb-8">
              <div class="flex justify-between text-sm">
                <span class="text-white/50">Sous-total</span>
                <span class="font-semibold text-white"
                  >{{ subtotal.toLocaleString() }} CFA</span
                >
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-white/50">Livraison</span>
                <span class="font-semibold" style="color: #10b981"
                  >Gratuite</span
                >
              </div>
              <div
                class="pt-4 flex justify-between"
                style="border-top: 1px solid rgba(255, 255, 255, 0.06)"
              >
                <span class="font-bold text-lg text-white">Total</span>
                <span
                  class="font-black text-2xl text-white"
                  style="font-family: 'Outfit', sans-serif"
                  >{{ total.toLocaleString() }} CFA</span
                >
              </div>
            </div>

            <!-- Promo Code -->
            <div class="mb-8">
              <label class="block text-sm font-semibold mb-3 text-white/70"
                >Code Promo</label
              >
              <div class="flex gap-2">
                <input
                  v-model="promoCode"
                  type="text"
                  placeholder="Entrez votre code"
                  class="flex-1 rounded-xl px-4 py-3 transition-all text-sm text-white"
                  style="
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                  "
                />
                <button
                  @click="applyPromoCode"
                  class="px-5 rounded-xl font-semibold transition-all hover:scale-105"
                  style="background: rgba(55, 182, 233, 0.15); color: #37b6e9"
                >
                  Appliquer
                </button>
              </div>
            </div>

            <!-- Checkout Button -->
            <RouterLink
              to="/checkout"
              class="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold transition-all hover:scale-[1.02] hover:shadow-xl active:scale-95"
              style="
                background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);
                color: white;
                box-shadow: 0 4px 20px rgba(55, 182, 233, 0.3);
              "
            >
              <span class="material-icons-outlined">lock</span>
              <span>Passer la commande</span>
            </RouterLink>

            <p
              class="text-xs text-white/40 text-center mt-4 flex items-center justify-center gap-1"
            >
              <span class="material-icons-outlined text-sm">verified_user</span>
              Paiement sécurisé
            </p>

            <!-- Trust Badges -->
            <div
              class="mt-8 pt-6 flex justify-center items-center gap-6"
              style="border-top: 1px solid rgba(255, 255, 255, 0.06)"
            >
              <div class="flex items-center gap-2 text-xs text-white/50">
                <span
                  class="material-icons-outlined text-sm"
                  style="color: #10b981"
                  >check_circle</span
                >
                <span>Livraison instantanée</span>
              </div>
              <div class="flex items-center gap-2 text-xs text-white/50">
                <span
                  class="material-icons-outlined text-sm"
                  style="color: #10b981"
                  >check_circle</span
                >
                <span>Support 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
